"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Trip = exports.tripAttributesWithComposedAttributes = exports.tripAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const uniq_1 = __importDefault(require("lodash/uniq"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const Result_type_1 = require("../../types/Result.type");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const VisitedPlace_1 = require("./VisitedPlace");
const Segment_1 = require("./Segment");
const Junction_1 = require("./Junction");
const StartEndable_1 = require("./StartEndable");
const PhysicsUtils_1 = require("../../utils/PhysicsUtils");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.tripAttributes = [
    ...StartEndable_1.startEndDateAndTimesAttributes,
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    '_sequence',
    'preData'
];
exports.tripAttributesWithComposedAttributes = [
    ...exports.tripAttributes,
    '_startPlace',
    '_endPlace',
    '_segments',
    '_junctions'
];
/**
 * A trip include the travelling action between two places (visited places: origin|destination)
 * Start and end dates and times could be generated from the origin and destination data
 */
class Trip extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, [
            '_startPlace',
            '_endPlace',
            '_segments',
            '_junctions',
            'startPlace',
            'endPlace',
            'segments',
            'junctions',
            '_journeyUuid',
            '_tripChainUuid'
        ]), exports.tripAttributes, exports.tripAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.startPlace = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._startPlace, VisitedPlace_1.VisitedPlace.unserialize, this._surveyObjectsRegistry);
        this.endPlace = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._endPlace, VisitedPlace_1.VisitedPlace.unserialize, this._surveyObjectsRegistry);
        this.segments = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._segments, Segment_1.Segment.unserialize, this._surveyObjectsRegistry);
        this.junctions = ConstructorUtils_1.ConstructorUtils.initializeComposedArrayAttributes(params._junctions, Junction_1.Junction.unserialize, this._surveyObjectsRegistry);
        this.journeyUuid = params._journeyUuid;
        this.tripChainUuid = params._tripChainUuid;
        this._surveyObjectsRegistry.registerTrip(this);
    }
    /**
     * Check if the trip has segments
     * @returns {boolean} - Returns true if the trip has segments, false otherwise
     */
    hasSegments() {
        return this.segments ? this.segments.length > 0 : false;
    }
    /**
     * Check if the trip has transit segments
     * @returns {boolean} - Returns true if the trip has at least one transit segment, false otherwise
     */
    hasTransit() {
        return this.segments ? this.segments.some((segment) => segment.isTransit()) : false;
    }
    /**
     * Get the modes from all segments
     * @returns {Mode[]} - Returns the modes used for the trip
     */
    getModes() {
        return this.segments ? this.segments.map((segment) => segment.mode) : [];
    }
    /**
     * Get the mode categories from all segments
     * @returns {ModeCategory[]} - Returns the mode categories used for the trip
     */
    getModeCategories() {
        return this.segments ? this.segments.map((segment) => segment.modeCategory) : [];
    }
    /**
     * Check if the trip is multimodal (more than one mode)
     * @returns {boolean} - Returns true if the trip is multimodal, false otherwise
     */
    isMultimodal() {
        return (0, uniq_1.default)(this.getModes()).length > 1;
    }
    /**
     * Get the modes without walking
     * @returns {Mode[]} - Returns the modes ignoring walking
     */
    getModesWithoutWalk() {
        return this.getModes().filter((mode) => mode !== 'walk');
    }
    /**
     * Get the transit modes
     * @returns {Mode[]} - Returns the transit modes, ignoring other modes
     */
    getTransitModes() {
        return this.segments
            ? this.segments.filter((segment) => segment.isTransit()).map((segment) => segment.mode)
            : [];
    }
    /**
     * Get the non transit modes
     * @returns {Mode[]} - Returns the non transit modes (ignoring transit modes)
     */
    getNonTransitModes() {
        return this.segments
            ? this.segments.filter((segment) => !segment.isTransit()).map((segment) => segment.mode)
            : [];
    }
    /**
     * Check if the trip is transit multimodal (at least one transit mode + another non-walking mode)
     * @returns {boolean} - Returns true if the trip is transit multimodal, false otherwise
     */
    isTransitMultimodal() {
        return this.getNonTransitModes().length > 1 && this.hasTransit();
    }
    /**
     * Check if the trip is transit only (no non-transit mode, ignore any walking mode though)
     * @returns {boolean} - Returns true if the trip is transit only, false otherwise
     */
    isTransitOnly() {
        return this.hasTransit() && this.getTransitModes().length === this.getModesWithoutWalk().length;
    }
    /**
     * Get the duration of the trip in seconds
     * endTime must be >= startTime and both must exist
     * @returns {Optional<number>} - Returns the duration in seconds, or undefined if no start or end time
     */
    getDurationSeconds() {
        return StartEndable_1.StartEndable.getDurationSeconds(this);
    }
    /**
     * Get the bird distance between the origin and destination in meters (euclidian distance or as the crow flies distance)
     * @returns {Optional<number>} - Returns the distance in meters, or undefined if no origin or destination or if the origin or destination is not a valid point
     */
    getBirdDistanceMeters() {
        var _a, _b;
        return (0, PhysicsUtils_1.getBirdDistanceMeters)((_a = this.startPlace) === null || _a === void 0 ? void 0 : _a.geography, (_b = this.endPlace) === null || _b === void 0 ? void 0 : _b.geography);
    }
    /**
     * Get the bird speed in km/h, rounded to 2 decimal places
     * @returns {Optional<number>} - Returns the speed in km/h, or undefined if no origin or destination or if the origin or destination is not a valid point
     */
    getBirdSpeedKph() {
        var _a, _b;
        return (0, PhysicsUtils_1.getBirdSpeedKph)((_a = this.startPlace) === null || _a === void 0 ? void 0 : _a.geography, (_b = this.endPlace) === null || _b === void 0 ? void 0 : _b.geography, this.getDurationSeconds());
    }
    /**
     * Setup the start and end times if they are not set
     */
    setupStartAndEndTimes() {
        var _a, _b;
        if (this.startTime === undefined && ((_a = this.startPlace) === null || _a === void 0 ? void 0 : _a.endTime)) {
            this.startTime = this.startPlace.endTime;
        }
        if (this.endTime === undefined && ((_b = this.endPlace) === null || _b === void 0 ? void 0 : _b.startTime)) {
            this.endTime = this.endPlace.startTime;
        }
    }
    get origin() {
        return this.startPlace;
    }
    set origin(value) {
        this.startPlace = value;
    }
    get destination() {
        return this.endPlace;
    }
    set destination(value) {
        this.endPlace = value;
    }
    /**
     * Whether the last questionnaire segment closed this trip's segment chain.
     *
     * `true` when the last segment answers `hasNextMode === false`.
     * `false` when the last segment did not close the chain, including when the answer is missing.
     */
    get isSegmentChainClosed() {
        return this._isSegmentChainClosed;
    }
    set isSegmentChainClosed(value) {
        this._isSegmentChainClosed = value;
    }
    /**
     * Whether more than one questionnaire segment closed this trip's segment chain.
     *
     * `true` when more than one segment answers `hasNextMode === false`.
     * `false` when at most one segment closed the chain.
     */
    get isSegmentChainClosedMoreThanOnce() {
        return this._isSegmentChainClosedMoreThanOnce;
    }
    set isSegmentChainClosedMoreThanOnce(value) {
        this._isSegmentChainClosedMoreThanOnce = value;
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get startDate() {
        return this._attributes.startDate;
    }
    set startDate(value) {
        this._attributes.startDate = value;
    }
    get startTime() {
        return this._attributes.startTime;
    }
    set startTime(value) {
        this._attributes.startTime = value;
    }
    get startTimePeriod() {
        return this._attributes.startTimePeriod;
    }
    set startTimePeriod(value) {
        this._attributes.startTimePeriod = value;
    }
    get endDate() {
        return this._attributes.endDate;
    }
    set endDate(value) {
        this._attributes.endDate = value;
    }
    get endTime() {
        return this._attributes.endTime;
    }
    set endTime(value) {
        this._attributes.endTime = value;
    }
    get endTimePeriod() {
        return this._attributes.endTimePeriod;
    }
    set endTimePeriod(value) {
        this._attributes.endTimePeriod = value;
    }
    get startPlace() {
        return this._startPlace;
    }
    set startPlace(value) {
        this._startPlace = value;
    }
    get endPlace() {
        return this._endPlace;
    }
    set endPlace(value) {
        this._endPlace = value;
    }
    get segments() {
        return this._segments;
    }
    set segments(value) {
        this._segments = value;
    }
    get junctions() {
        return this._junctions;
    }
    set junctions(value) {
        this._junctions = value;
    }
    get journeyUuid() {
        return this._journeyUuid;
    }
    set journeyUuid(value) {
        this._journeyUuid = value;
    }
    get journey() {
        if (!this._journeyUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getJourney(this._journeyUuid);
    }
    get tripChainUuid() {
        return this._tripChainUuid;
    }
    set tripChainUuid(value) {
        this._tripChainUuid = value;
    }
    get tripChain() {
        if (!this._tripChainUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getTripChain(this._tripChainUuid);
    }
    get person() {
        var _a;
        return (_a = this.journey) === null || _a === void 0 ? void 0 : _a.person;
    }
    get household() {
        var _a, _b;
        return (_b = (_a = this.journey) === null || _a === void 0 ? void 0 : _a.person) === null || _b === void 0 ? void 0 : _b.household;
    }
    /**
     * Add a segment to this trip
     */
    addSegment(segment) {
        if (!this._segments) {
            this._segments = [];
        }
        this._segments.push(segment);
    }
    /**
     * Insert a segment at a specific index
     */
    insertSegment(segment, index) {
        if (!this._segments) {
            this._segments = [];
        }
        this._segments.splice(index, 0, segment);
    }
    /**
     * Insert a segment after another segment with the specified UUID
     */
    insertSegmentAfterUuid(segment, afterUuid) {
        if (!this._segments) {
            this._segments = [];
        }
        // If array is empty, add the segment
        if (this._segments.length === 0) {
            this._segments.push(segment);
            return true;
        }
        const index = this._segments.findIndex((s) => s._uuid === afterUuid);
        if (index >= 0) {
            this._segments.splice(index + 1, 0, segment);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Insert a segment before another segment with the specified UUID
     */
    insertSegmentBeforeUuid(segment, beforeUuid) {
        if (!this._segments) {
            this._segments = [];
        }
        // If array is empty, add the segment
        if (this._segments.length === 0) {
            this._segments.push(segment);
            return true;
        }
        const index = this._segments.findIndex((s) => s._uuid === beforeUuid);
        if (index >= 0) {
            this._segments.splice(index, 0, segment);
            return true;
        }
        // If UUID not found in non-empty array, return false
        return false;
    }
    /**
     * Get segments without walking segments in multimodal trips
     * Walking segments are implicit and excluded unless the entire trip is on foot
     *
     * Why: in a multimodal trip, walking is always implied — you walk to reach the car,
     * the bus stop, the train platform, the motorcycle or the bicycle. OD surveys have
     * always removed or ignored those walking segments, and most respondents would not
     * declare them manually anyway, so keeping them would make trips inconsistent
     * depending on how thorough the respondent was. This is a survey-wide convention
     * rather than an export option; making it configurable would need its own issue.
     *
     * @returns Array of segments with walking segments filtered out if other modes exist
     */
    getSegmentsWithoutWalkingInMultimode() {
        const segments = this.segments || [];
        if (segments.length === 0) {
            return segments;
        }
        // Get unique modes to determine if this is truly multimodal, we don't want to filter walk,walk for instance (this would be audited though)
        const uniqueModes = new Set(segments.map((segment) => segment.mode));
        // Filter out walking segments if there are multiple unique modes and non-walking modes exist
        const newSegmentsWithoutWalking = uniqueModes.size > 1 && segments.some((segment) => segment.mode !== 'walk')
            ? segments.filter((segment) => segment.mode !== 'walk')
            : segments;
        return newSegmentsWithoutWalking;
    }
    /**
     * Remove a segment from this trip by UUID
     */
    removeSegment(segmentUuid) {
        if (!this._segments) {
            return false;
        }
        const index = this._segments.findIndex((segment) => segment._uuid === segmentUuid);
        if (index >= 0) {
            this._segments.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Get a segment by UUID
     */
    getSegmentByUuid(segmentUuid) {
        if (!this._segments) {
            return undefined;
        }
        return this._segments.find((segment) => segment._uuid === segmentUuid);
    }
    /**
     * Creates a Trip object from sanitized parameters
     * @param {ExtendedTripAttributes | SerializedExtendedTripAttributes} params - Sanitized trip parameters
     * @returns {Trip} New Trip instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Trip(flattenedParams, surveyObjectsRegistry);
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = Trip.validateParams(dirtyParams);
        const trip = errors.length === 0 ? new Trip(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(trip);
    }
    static validateParams(dirtyParams, displayName = 'Trip') {
        const errors = [];
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams, displayName));
        errors.push(...StartEndable_1.StartEndable.validateParams(dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('_sequence', dirtyParams._sequence, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        const startPlaceAttributes = dirtyParams.startPlace;
        if (startPlaceAttributes !== undefined) {
            errors.push(...VisitedPlace_1.VisitedPlace.validateParams(startPlaceAttributes, 'StartVisitedPlace'));
        }
        const endPlaceAttributes = dirtyParams.endPlace;
        if (endPlaceAttributes !== undefined) {
            errors.push(...VisitedPlace_1.VisitedPlace.validateParams(endPlaceAttributes, 'EndVisitedPlace'));
        }
        const segmentsAttributes = dirtyParams._segments !== undefined ? dirtyParams._segments : [];
        for (let i = 0, countI = segmentsAttributes.length; i < countI; i++) {
            const segmentAttributes = segmentsAttributes[i];
            errors.push(...Segment_1.Segment.validateParams(segmentAttributes, `Segment ${i}`));
        }
        const junctionsAttributes = dirtyParams._junctions !== undefined ? dirtyParams._junctions : [];
        for (let i = 0, countI = junctionsAttributes.length; i < countI; i++) {
            const junctionAttributes = junctionsAttributes[i];
            errors.push(...Junction_1.Junction.validateParams(junctionAttributes, `Junction ${i}`));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_journeyUuid', dirtyParams._journeyUuid, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_tripChainUuid', dirtyParams._tripChainUuid, displayName));
        return errors;
    }
}
exports.Trip = Trip;
Trip._confidentialAttributes = ['preData'];
//# sourceMappingURL=Trip.js.map